/**
 * Sign in.
 *
 * Deployed, this is one Yahoo button — the league already lives on Yahoo, so it is the account
 * every manager definitely has, and the same consent can later carry a Fantasy API token.
 *
 * Offline (the single-file preview, and the draft-night fallback if Firebase is unreachable)
 * there is no identity provider to talk to, so it becomes a GM picker instead. Same Session
 * shape either way, so no screen downstream has to care which one ran.
 */

import { currentManagers, managerShort } from '../domain/identity.js';
import { GMS, ROLE_LABEL, VIEWERS, roleFor, useSession } from '../store/session.js';

/**
 * Who to go and ask.
 *
 * Read off the roster rather than written down, because the previous version of this line named
 * the commissioner — and pairing an account is an ADMIN power, which is a different person. A
 * hardcoded name is also the sort of thing that stays wrong for a year after the job changes
 * hands. If there is ever more than one admin, name them all; if somehow none, fall back to a
 * description rather than an empty sentence.
 */
function whoToAsk(): string {
  const admins = currentManagers().filter((m) => m.role === 'admin').map((m) => managerShort(m.id));
  if (admins.length === 0) return 'an admin';
  if (admins.length === 1) return admins[0]!;
  return `${admins.slice(0, -1).join(', ')} or ${admins.at(-1)}`;
}

export function SignIn() {
  const { live, signIn, actAs, session } = useSession();

  return (
    <div className="viz-root shell">
      <div className="signin">
        <div className="brand big">
          <span className="mark" aria-hidden="true">◆</span>
          <span>League Manager</span>
          <span className="season">2026</span>
        </div>

        {live ? (
          <>
            <p className="sub">Sign in with the Yahoo account you use for the league.</p>
            <button type="button" className="btn primary big" onClick={() => void signIn()}>
              Continue with Yahoo
            </button>
            {/*
              TWO DIFFERENT ANSWERS, because "we have not got to you yet" and "we looked at this
              and said no" are not the same message. Telling somebody who has been turned away to
              go and ask the admin to link his account sends him to ask for something that has
              already been decided.
            */}
            {session && !session.gm && (
              session.link?.rejected ? (
                <p className="note bad">
                  You are signed in as <b>{session.displayName}</b>, but this account has not been
                  given access to the league. If you think that is wrong, speak to {whoToAsk()} —
                  signing in again will not change it.
                </p>
              ) : (
                <p className="note bad">
                  You are signed in as <b>{session.displayName}</b>, but that account is not linked
                  to a team yet. Ask {whoToAsk()} to link it and reload.
                </p>
              )
            )}
          </>
        ) : (
          <>
            <p className="sub">
              Preview mode — no sign-in available offline. Pick a manager to see the app as
              they would.
            </p>
            <div className="gmgrid">
              {GMS.map((g) => (
                <button key={g} type="button" className={`gmpick role-${roleFor(g)}`}
                  title={ROLE_LABEL[roleFor(g)]} onClick={() => actAs(g)}>
                  {g}
                  {roleFor(g) !== 'manager' && <span className="ico" aria-hidden="true"> ★</span>}
                </button>
              ))}
              {/*
                Viewers are not in GMS, because GMS is the ten men holding a team and a viewer
                holds none — which is the point of him. He still belongs in this picker: the
                preview is only honest if the least-privileged account in the league can be
                looked at, and "what does Neel actually see?" is a question worth being able to
                answer without signing in as Neel.
              */}
              {VIEWERS.map((g) => (
                <button key={g} type="button" className="gmpick role-viewer"
                  title={ROLE_LABEL.viewer} onClick={() => actAs(g)}>
                  {g}<span className="ico" aria-hidden="true"> ◉</span>
                </button>
              ))}
            </div>
            <p className="note">
              <span className="ico role-admin">★</span> admin (everything, and can act for any
              team) · <span className="ico role-commissioner">★</span> commissioner (league-wide
              review and controls) · <span className="ico role-viewer">◉</span> viewer (reads
              everything, holds no team, acts on nothing)
            </p>
          </>
        )}
      </div>
    </div>
  );
}
